'use client';

import { api } from '@/api/client';
import { AdmissionBadge } from '@/components/blocks/badges/AdmissionBadge';
import { InPersonBadge } from '@/components/blocks/badges/InPersonBadge';
import { OnlineBadge } from '@/components/blocks/badges/OnlineBadge';
import { ImageFallback } from '@/components/blocks/ImageFallback';
import { SpeakerChip } from '@/components/blocks/SpeakerChip';
import { ExternalRedirectButton } from '@/components/blocks/buttons/ExternalRedirectButton';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { buttonVariants } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { AddToCalendar } from '@/features/AddToCalendar';
import {
  RsvpAttendButton,
  RsvpDialog,
  formatUserRsvpMessage,
  isUserRegisteredForEvent,
} from '@/features/EventRsvp';
import { useAuth } from '@/hooks/useAuth';
import { CenteredPageLayout } from '@/layout/CenteredPageLayout';
import { MortgageCard } from '@/views/account/mortgages/MortgageCard';
import { PropertyCard } from '@/views/account/properties/PropertyCard';
import type { Event, Mortgage, Property } from '@mr-website/schemas';
import { UserBookmarkStatusEnum } from '@mr-website/schemas';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, ChevronLeft, MapPin, Monitor } from 'lucide-react';
import { useState } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { EventCardSkeleton } from '../events/EventCardSkeleton';
import { EventDetailSkeleton } from './EventDetailSkeleton';

function formatDate(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(iso));
}

function formatTime(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(iso));
}

function formatDuration(start: string, end: string): string {
  const minutes = (new Date(end).getTime() - new Date(start).getTime()) / 60000;
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = minutes / 60;
  if (hours < 24) {
    const rounded = Math.round(hours * 2) / 2;
    return rounded === 1 ? '1 hr' : `${rounded} hrs`;
  }
  const days = hours / 24;
  const rounded = Math.round(days * 2) / 2;
  return rounded === 1 ? '1 day' : `${rounded} days`;
}

interface EventDetailProps {
  initialEvent?: Event | null;
}

export default function EventDetail({ initialEvent }: EventDetailProps) {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated } = useAuth();
  const [rsvpOpen, setRsvpOpen] = useState(false);

  const accountCenterUrl = process.env.NEXT_PUBLIC_ACCOUNT_CENTER_URL;

  const {
    data: event,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['event', id],
    queryFn: async () => {
      const { data } = await api.get<Event>(`/events/${id}`);
      return data;
    },
    initialData: initialEvent ?? undefined,
    enabled: !!id,
  });

  const { data: relatedProperties, isLoading: propertiesLoading } = useQuery({
    queryKey: ['event-properties', id, event?.markets],
    queryFn: async () => {
      const params = new URLSearchParams();
      (event?.markets ?? []).forEach((m) => params.append('markets', m));
      const { data } = await api.get<Property[]>(`/events/${id}/properties?${params}`);
      return data;
    },
    enabled: !!id && !!event,
  });

  const { data: relatedMortgages, isLoading: mortgagesLoading } = useQuery({
    queryKey: ['event-mortgages', id],
    queryFn: async () => {
      const { data } = await api.get<Mortgage[]>(`/events/${id}/mortgages`);
      return data;
    },
    enabled: !!id && !!event,
  });

  if (isLoading) {
    return <EventDetailSkeleton />;
  }

  if (isError || !event) {
    return <p className="text-sm text-destructive">Could not load event.</p>;
  }

  const displayRsvp = Math.max(7, event.rsvp_count);
  const hasDuration = event.date_end && event.date_end !== event.date_start;
  const isRegistered = isUserRegisteredForEvent(event);

  function handleRsvpClick() {
    if (!isAuthenticated) {
      const redirect = encodeURIComponent(window.location.href);
      window.location.assign(`${accountCenterUrl}/login?redirect=${redirect}`);
      return;
    }
    setRsvpOpen(true);
  }

  return (
    <CenteredPageLayout>
      <Link
        href="/events"
        className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ChevronLeft className="size-4" aria-hidden />
        Back to Events
      </Link>
      {/* Mobile title: shown only on mobile, above the sidebar */}
      <div className="mb-4 lg:hidden">
        <h1 className="text-3xl font-bold leading-snug text-foreground">{event.name}</h1>
        {event.type ? (
          <p className="text-data-card-sm mt-1 font-bold capitalize text-mr-teal-light">
            {event.type.replace(/[_-]/g, ' ')}
          </p>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px] lg:gap-12">
        {/* Left: image (desktop top) + desktop title + description (image last on mobile) */}
        <div className="order-2 flex min-w-0 flex-col gap-6 lg:order-1">
          {/* Image: shown last on mobile via order-last, first on desktop */}
          <div className="relative order-last lg:order-first">
            <AspectRatio ratio={16 / 9} className="overflow-hidden rounded-xl">
              {event.featured_photo ? (
                <img
                  src={event.featured_photo}
                  alt={event.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <ImageFallback subTitle={event.type} iconSize={40} className="h-full w-full" />
              )}
            </AspectRatio>
            <div className="absolute bottom-3 left-3 z-10 flex items-center gap-1.5">
              {event.is_online ? <OnlineBadge /> : <InPersonBadge />}
              <AdmissionBadge isPrivate={event.is_private} admissionFee={event.admission_fee} />
            </div>
          </div>

          {/* Desktop title (hidden on mobile since it's shown above) */}
          <div className="hidden lg:block">
            <h1 className="text-3xl font-bold leading-snug text-foreground">{event.name}</h1>
            {event.type ? (
              <p className="text-data-card-sm mt-1 text-mr-teal-light font-bold capitalize">
                {event.type.replace(/[_-]/g, ' ')}
              </p>
            ) : null}
          </div>

          {/*
           * Description
           *
           * dangerouslySetInnerHTML is safe here because
           * the HTML content originates from the CRM.
           *
           */}
          {event.description ? (
            <>
              <h2 className="sr-only">About This Event</h2>
              <div
                className="prose prose-sm max-w-none text-foreground"
                dangerouslySetInnerHTML={{ __html: event.description }}
              />
            </>
          ) : null}
        </div>

        {/* Right: sidebar card — order-1 on mobile (before left column), sticky on desktop */}
        <Card className="order-1 h-fit gap-0 py-0 lg:order-2 lg:sticky lg:top-24">
          {/* Date & Time */}
          <CardContent className="py-4">
            <h2 className="sr-only">Event Details</h2>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Date &amp; Time
            </p>
            <p className="text-sm font-semibold text-foreground">
              {formatDate(event.date_start, event.time_zone)}
            </p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {formatTime(event.date_start, event.time_zone)}
              {hasDuration
                ? ` - ${formatTime(event.date_end, event.time_zone)} (${formatDuration(event.date_start, event.date_end)})`
                : null}
            </p>
            <AddToCalendar event={event} />
          </CardContent>

          <Separator />

          {/* Location */}
          <CardContent className="py-4">
            <h2 className="sr-only">Event Location</h2>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Location
            </p>
            {event.is_online ? (
              <>
                <div className="flex items-center gap-2">
                  <Monitor className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                  <span className="text-sm font-semibold text-foreground">Online Presentation</span>
                </div>
                {event.user_rsvp?.goto_join_url ? (
                  <ExternalRedirectButton
                    href={event.user_rsvp.goto_join_url}
                    label="Join Webinar"
                    className="mt-2 w-full"
                    newTab
                  />
                ) : (
                  <p className="mt-1 text-xs text-muted-foreground">
                    A webinar link will appear here after you RSVP.
                  </p>
                )}
              </>
            ) : event.address ? (
              <div className="flex flex-col gap-1">
                {event.location_name && (
                  <p className="text-sm font-semibold text-foreground">{event.location_name}</p>
                )}
                <div className="flex items-start gap-2">
                  <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(event.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-semibold text-foreground underline underline-offset-2 transition-opacity hover:opacity-70"
                  >
                    {event.address}
                  </a>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <MapPin className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="text-sm text-muted-foreground">Location TBD</span>
              </div>
            )}
          </CardContent>

          <Separator />

          {/* Parking */}
          {!event.is_online && event.parking_instructions && (
            <>
              <CardContent className="py-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Parking Instructions
                </p>
                <div
                  className="prose prose-sm max-w-none text-foreground"
                  dangerouslySetInnerHTML={{ __html: event.parking_instructions }}
                />
              </CardContent>

              <Separator />
            </>
          )}

          <Separator />

          {/* Admission */}
          <CardContent className="flex items-center justify-between py-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Admission
            </p>
            <AdmissionBadge isPrivate={event.is_private} admissionFee={event.admission_fee} />
          </CardContent>

          <Separator />

          {/* Speakers */}
          {event.speakers.length > 0 ? (
            <>
              <CardContent className="py-4">
                <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Speakers
                </h2>
                <div className="flex  flex-col gap-3">
                  {event.speakers.map((speaker) => (
                    <SpeakerChip
                      key={speaker.name}
                      name={speaker.name}
                      headshotUrl={speaker.headshot_image_url}
                      link={
                        speaker.agent_website_id
                          ? `/${speaker.agent_website_id}`
                          : (speaker.linkedin_url ?? undefined)
                      }
                      title={speaker.job_title}
                    />
                  ))}
                </div>
              </CardContent>
              <Separator />
            </>
          ) : null}

          {/* RSVP button + attendee count */}
          <CardContent className="space-y-3 py-4">
            <div className="space-y-3">
              {isRegistered ? (
                <div className="flex items-center justify-center gap-2 text-center text-sm text-emerald-700">
                  <CheckCircle2 className="size-4 shrink-0" aria-hidden />
                  <span>
                    {event.user_rsvp ? formatUserRsvpMessage(event.user_rsvp) : "You have RSVP'd"}
                  </span>
                </div>
              ) : null}
              <RsvpAttendButton
                event={event}
                isAuthenticated={isAuthenticated}
                size="lg"
                className="w-full font-bold text-white"
                style={{ backgroundColor: 'var(--color-mr-teal-dark)' }}
                onClick={handleRsvpClick}
              />
            </div>
            <div className="flex items-center justify-center gap-2">
              <span className="size-2 shrink-0 rounded-full bg-emerald-500" aria-hidden />
              <span className="text-sm text-muted-foreground">{displayRsvp} others going</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Related Properties */}
      {(propertiesLoading || (relatedProperties && relatedProperties.length > 0)) && (
        <section className="space-y-4 pt-8">
          <Separator />
          <div className="pt-2">
            <h2 className="text-xl font-bold text-foreground">Related Properties</h2>
            <p className="text-sm text-muted-foreground">
              Explore investment properties from Marshall Reddick Real Estate.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {propertiesLoading
              ? Array.from({ length: 3 }).map((_, i) => <EventCardSkeleton key={i} />)
              : relatedProperties!.map((p) => (
                  <PropertyCard
                    key={p.id}
                    property={{
                      ...p,
                      status: UserBookmarkStatusEnum.STAR,
                      date: new Date().toISOString(),
                    }}
                    showBookmark={false}
                  />
                ))}
          </div>
          {!propertiesLoading && (
            <div className="flex justify-center">
              <a href="/properties" className={buttonVariants({ variant: 'outline' })}>
                See All Available Properties
              </a>
            </div>
          )}
        </section>
      )}

      {/* Related Mortgages */}
      {(mortgagesLoading || (relatedMortgages && relatedMortgages.length > 0)) && (
        <section className="space-y-4 pt-8">
          <Separator />
          <div className="pt-2">
            <h2 className="text-xl font-bold text-foreground">Related Mortgages</h2>
            <p className="text-sm text-muted-foreground">
              Explore mortgage investment opportunities from Marshall Reddick Real Estate.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {mortgagesLoading
              ? Array.from({ length: 3 }).map((_, i) => <EventCardSkeleton key={i} />)
              : relatedMortgages!.map((m) => (
                  <MortgageCard
                    key={m.loanId}
                    mortgage={{
                      ...m,
                      status: UserBookmarkStatusEnum.STAR,
                      is_user_lender: false,
                      date: new Date().toISOString(),
                    }}
                    showBookmark={false}
                  />
                ))}
          </div>
          {!mortgagesLoading && (
            <div className="flex justify-center">
              <a href="/mortgages" className={buttonVariants({ variant: 'outline' })}>
                See All Available Mortgages
              </a>
            </div>
          )}
        </section>
      )}
      <RsvpDialog
        event={event}
        open={rsvpOpen}
        onOpenChange={setRsvpOpen}
        isManagingRsvp={isRegistered}
      />
    </CenteredPageLayout>
  );
}
